import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { 
  Crown, Building2, Users, Calendar, Search, RefreshCw, Loader2,
  DollarSign, TrendingUp, CheckCircle, X, Edit, Trash2, Save
} from 'lucide-react';
import { toast } from 'sonner';
import authAxios, { API } from '../../utils/authAxios';

const PLAN_COLORS = {
  gratuito: 'bg-slate-600',
  destacado: 'bg-blue-500',
  premium: 'bg-amber-500',
  elite: 'bg-purple-500',
  referido: 'bg-emerald-500'
};

const PLAN_LEVELS = {
  gratuito: 0,
  destacado: 1,
  premium: 2,
  elite: 3,
  referido: 2 // Same visibility as premium
};

const ENTITY_ICONS = {
  center: Building2,
  professional: Users,
  workshop: Calendar
};

function formatCurrency(amount) {
  return '$' + amount.toLocaleString('es-CL');
}

export default function AdminSubscriptions() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ type: 'all', status: 'all', search: '' });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ plan_type: '', duration_months: 1, notes: '' });

  async function loadData() {
    setLoading(true);
    try {
      const [subsRes, statsRes] = await Promise.all([
        authAxios.get(`${API}/admin/subscriptions?entity_type=${filter.type}&status=${filter.status}`, { withCredentials: true }),
        authAxios.get(`${API}/admin/subscriptions/stats`, { withCredentials: true })
      ]);
      setSubscriptions(subsRes.data.subscriptions || []);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Error al cargar suscripciones');
      console.error(error);
    }
    setLoading(false);
  }

  useEffect(() => {
    loadData();
  }, [filter.type, filter.status]);

  async function handleUpdateSubscription(entityType, entityId) {
    try {
      const planLevel = PLAN_LEVELS[editForm.plan_type] || 0;
      await authAxios.put(
        `${API}/admin/subscriptions/${entityType}/${entityId}`,
        {
          plan_type: editForm.plan_type,
          plan_level: planLevel,
          duration_months: editForm.duration_months,
          notes: editForm.notes
        },
        { withCredentials: true }
      );
      toast.success('Suscripción actualizada');
      setEditingId(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    }
  }

  async function handleCancelSubscription(entityType, entityId, name) {
    if (!confirm(`¿Cancelar suscripción de "${name}"? Se cambiará a plan Gratuito.`)) return;
    
    try {
      await authAxios.delete(
        `${API}/admin/subscriptions/${entityType}/${entityId}`,
        { withCredentials: true }
      );
      toast.success('Suscripción cancelada');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cancelar');
    }
  }

  const filteredSubscriptions = subscriptions.filter(sub => {
    if (filter.search) {
      const search = filter.search.toLowerCase();
      return (
        sub.name?.toLowerCase().includes(search) ||
        sub.email?.toLowerCase().includes(search) ||
        sub.city?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-purple-500/10 to-slate-900 border-purple-500/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <Crown className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-50">
                    {stats.centers.elite + stats.centers.premium + stats.centers.destacado}
                  </p>
                  <p className="text-xs text-slate-400">Centros Pagando</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-500/10 to-slate-900 border-amber-500/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500/20 rounded-lg">
                  <Users className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-50">
                    {stats.professionals.premium + stats.professionals.destacado}
                  </p>
                  <p className="text-xs text-slate-400">Profesionales Pagando</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500/10 to-slate-900 border-green-500/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <DollarSign className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-50">
                    {formatCurrency(stats.revenue_estimate.total)}
                  </p>
                  <p className="text-xs text-slate-400">Ingreso Est./Mes</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Revenue Breakdown */}
      {stats && (
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-slate-50 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
              Desglose de Ingresos Estimados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-4 bg-slate-800/50 rounded-lg">
                <p className="text-slate-400 text-sm mb-1">Centros</p>
                <p className="text-xl font-bold text-slate-50">{formatCurrency(stats.revenue_estimate.centers)}</p>
                <div className="mt-2 space-y-1 text-xs text-slate-500">
                  <p>Elite: {stats.centers.elite} × $350.000</p>
                  <p>Premium: {stats.centers.premium} × $199.000</p>
                  <p>Destacado: {stats.centers.destacado} × $99.000</p>
                </div>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-lg">
                <p className="text-slate-400 text-sm mb-1">Profesionales</p>
                <p className="text-xl font-bold text-slate-50">{formatCurrency(stats.revenue_estimate.professionals)}</p>
                <div className="mt-2 space-y-1 text-xs text-slate-500">
                  <p>Premium: {stats.professionals.premium} × $49.000</p>
                  <p>Destacado: {stats.professionals.destacado} × $29.000</p>
                </div>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-lg">
                <p className="text-slate-400 text-sm mb-1">Talleres</p>
                <p className="text-xl font-bold text-slate-50">{formatCurrency(stats.revenue_estimate.workshops)}</p>
                <div className="mt-2 space-y-1 text-xs text-slate-500">
                  <p>Destacado: {stats.workshops.destacado} × $19.000</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  placeholder="Buscar por nombre, email o ciudad..."
                  value={filter.search}
                  onChange={(e) => setFilter({ ...filter, search: e.target.value })}
                  className="pl-10 bg-slate-950 border-slate-700"
                />
              </div>
            </div>
            
            <Select value={filter.type} onValueChange={(v) => setFilter({ ...filter, type: v })}>
              <SelectTrigger className="w-[150px] bg-slate-950 border-slate-700">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="centers">Centros</SelectItem>
                <SelectItem value="professionals">Profesionales</SelectItem>
                <SelectItem value="workshops">Talleres</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v })}>
              <SelectTrigger className="w-[150px] bg-slate-950 border-slate-700">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="active">Solo Activos</SelectItem>
              </SelectContent>
            </Select>

            <Button onClick={loadData} variant="outline" className="border-slate-700">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Subscriptions Table */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-slate-50">
            Suscripciones ({filteredSubscriptions.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
            </div>
          ) : (
            <div className="space-y-2">
              {filteredSubscriptions.map((sub) => {
                const Icon = ENTITY_ICONS[sub.entity_type] || Building2;
                const isEditing = editingId === `${sub.entity_type}-${sub.entity_id}`;
                
                return (
                  <div 
                    key={`${sub.entity_type}-${sub.entity_id}`}
                    className="flex items-center gap-4 p-4 bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-colors"
                  >
                    <div className="p-2 bg-slate-700 rounded-lg">
                      <Icon className="w-5 h-5 text-slate-400" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-50 truncate">{sub.name}</p>
                      <p className="text-sm text-slate-400 truncate">
                        {sub.email || sub.city || 'Sin info'}
                      </p>
                    </div>

                    {isEditing ? (
                      <div className="flex items-center gap-2">
                        <Select 
                          value={editForm.plan_type} 
                          onValueChange={(v) => setEditForm({ ...editForm, plan_type: v })}
                        >
                          <SelectTrigger className="w-[130px] bg-slate-950 border-slate-700 text-sm">
                            <SelectValue placeholder="Plan" />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-900 border-slate-800">
                            <SelectItem value="gratuito">Gratuito</SelectItem>
                            <SelectItem value="destacado">Destacado</SelectItem>
                            <SelectItem value="premium">Premium</SelectItem>
                            {sub.entity_type === 'center' && (
                              <SelectItem value="elite">Elite</SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                        
                        <Select 
                          value={String(editForm.duration_months)} 
                          onValueChange={(v) => setEditForm({ ...editForm, duration_months: parseInt(v) })}
                        >
                          <SelectTrigger className="w-[100px] bg-slate-950 border-slate-700 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-900 border-slate-800">
                            <SelectItem value="1">1 mes</SelectItem>
                            <SelectItem value="3">3 meses</SelectItem>
                            <SelectItem value="6">6 meses</SelectItem>
                            <SelectItem value="12">12 meses</SelectItem>
                          </SelectContent>
                        </Select>

                        <Button 
                          size="sm" 
                          onClick={() => handleUpdateSubscription(sub.entity_type, sub.entity_id)}
                          className="bg-emerald-500 hover:bg-emerald-600"
                        >
                          <Save className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => setEditingId(null)}
                          className="text-slate-400"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Badge className={`${PLAN_COLORS[sub.plan_type] || PLAN_COLORS.gratuito} text-white`}>
                          {sub.plan_type?.charAt(0).toUpperCase() + sub.plan_type?.slice(1) || 'Gratuito'}
                        </Badge>
                        
                        {sub.premium_until && (
                          <span className="text-xs text-slate-500">
                            Vence: {new Date(sub.premium_until).toLocaleDateString('es-CL')}
                          </span>
                        )}

                        <div className="flex items-center gap-1">
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => {
                              setEditingId(`${sub.entity_type}-${sub.entity_id}`);
                              setEditForm({ 
                                plan_type: sub.plan_type || 'gratuito', 
                                duration_months: 1,
                                notes: ''
                              });
                            }}
                            className="text-slate-400 hover:text-slate-50"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          {sub.plan_level > 0 && (
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => handleCancelSubscription(sub.entity_type, sub.entity_id, sub.name)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}

              {filteredSubscriptions.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  No se encontraron suscripciones
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
